import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  InboxOutlined,
  NotificationOutlined,
  StopOutlined,
} from '@ant-design/icons';

import { Bento, StatTile } from '../../../components/shared';
import { Skeleton } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { soVN } from '../../../lib/dinh-dang';
import { ICON_KENH } from '../constants';
import { useTongQuan } from '../hooks/useCanhBao';
import { useDichCanhBao } from '../hooks/useDichCanhBao';
import { NhatKyCanhBao } from './NhatKyCanhBao';
import { VungLoi } from './VungLoi';

/** Tab "Giám sát gửi" của cổng nền tảng: số liệu 24 giờ và nhật ký của mọi tenant, lọc theo tenant. */
export function GiamSatGui() {
  const { t, locale } = useT();
  const dich = useDichCanhBao();
  const dichLoi = useLoi();
  const q = useTongQuan();
  const o = q.data;
  const so = (n: number | undefined) => (o ? soVN(n ?? 0, locale) : '—');

  return (
    <div className="space-y-4">
      {q.error && !o ? (
        <div className="overflow-hidden rounded-lg border border-default bg-surface shadow-sm">
          <VungLoi cau={t('canhBaoNenTang.loiTongQuan', { loi: dichLoi(q.error) })} onThuLai={() => void q.refetch()} />
        </div>
      ) : q.isLoading ? (
        <div className="grid gap-3 sm:grid-cols-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : (
        <>
          <Bento>
            <StatTile
              span="sm:col-span-4 lg:col-span-2"
              nhan={t('canhBaoNenTang.kpiCanhBao')}
              so={so(o?.records_24h)}
              icon={<NotificationOutlined />}
              tone="primary"
            />
            <StatTile
              span="sm:col-span-4 lg:col-span-2"
              nhan={t('canhBaoNenTang.kpiDaGui')}
              so={so(o?.sent_24h)}
              icon={<CheckCircleOutlined />}
              tone="success"
            />
            <StatTile
              span="sm:col-span-4 lg:col-span-2"
              nhan={t('canhBaoNenTang.kpiLoi')}
              so={so(o?.failed_24h)}
              icon={<CloseCircleOutlined />}
              tone="danger"
            />
            <StatTile
              span="sm:col-span-4 lg:col-span-2"
              nhan={t('canhBaoNenTang.kpiChan')}
              so={so(o?.suppressed_24h)}
              icon={<StopOutlined />}
              tone="neutral"
            />
            <StatTile
              span="sm:col-span-4 lg:col-span-2"
              nhan={t('canhBaoNenTang.kpiCho')}
              so={so(o?.pending_deliveries)}
              icon={<ClockCircleOutlined />}
              tone="warning"
            />
            <StatTile
              span="sm:col-span-4 lg:col-span-2"
              nhan={t('canhBaoNenTang.kpiHetLuot')}
              so={so(o?.dead_lettered_24h)}
              icon={<InboxOutlined />}
              tone="danger"
            />
          </Bento>
          {o && o.by_channel.length > 0 && (
            <section className="rounded-lg border border-default bg-surface px-4 py-3 shadow-sm">
              <h3 className="text-sm font-semibold text-content-primary">{t('canhBaoNenTang.theoKenh')}</h3>
              <ul className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {o.by_channel.map((c) => {
                  const Icon = ICON_KENH[c.channel_type];
                  return (
                    <li key={c.channel_type} className="flex min-w-0 items-center gap-2 text-sm">
                      <Icon className="text-content-muted" />
                      <span className="font-medium text-content-primary">{dich.tenKenh(c.channel_type)}</span>
                      <span className="min-w-0 truncate text-xs text-content-muted">
                        {t('canhBaoNenTang.theoKenhDong', {
                          dat: soVN(c.succeeded, locale),
                          loi: soVN(c.failed, locale),
                        })}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}
        </>
      )}
      <NhatKyCanhBao pv="nen-tang" giamSat duocSua />
    </div>
  );
}
